import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicCredentialReturnAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ShieldCheck, AlertCircle, CheckCircle, UploadCloud } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { LabeledDate, StagedUpload } from '../components/credential/CredentialControls';

const STEPS = { EMAIL: 'email', OTP: 'otp', ITEMS: 'items', EXPIRED: 'expired' };
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export default function PublicCredentialReturn() {
  const { token } = useParams();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [scopedToken, setScopedToken] = useState('');
  const [info, setInfo] = useState(null);
  const [data, setData] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicCredentialReturnAPI.info(token);
        setInfo(data);
      } catch (e) {
        setStep(STEPS.EXPIRED);
        setErrMsg(e.response?.data?.detail || 'This upload link is not valid.');
      }
    })();
  }, [token]);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!email) { toast.error('Enter your email to continue'); return; }
    setBusy(true); setErrMsg('');
    try {
      await publicCredentialReturnAPI.requestOtp(token, email.trim());
      toast.success('A 6-digit passcode was emailed to you.');
      setStep(STEPS.OTP);
    } catch (e2) {
      setErrMsg(e2.response?.data?.detail || 'Unable to send passcode');
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp) { toast.error('Enter the passcode'); return; }
    setBusy(true); setErrMsg('');
    try {
      const { data: tok } = await publicCredentialReturnAPI.verifyOtp(token, email.trim(), otp.trim());
      setScopedToken(tok.access_token);
      const { data: items } = await publicCredentialReturnAPI.items(token, tok.access_token);
      setData(items);
      setStep(STEPS.ITEMS);
    } catch (e2) {
      setErrMsg(e2.response?.data?.detail || 'Invalid passcode');
    } finally { setBusy(false); }
  };

  return (
    <div className="theme-pin-light min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-4">
        <div className="text-center">
          <img
            src="/tricorps360-horizontal.png"
            alt="TriCorps 360"
            className="h-10 w-auto mx-auto mb-4"
            data-testid="return-brand-logo"
          />
          <h1 className="text-xl font-semibold text-slate-800">Replace a document</h1>
          {info?.employee_name && <p className="text-sm text-slate-500">{info.employee_name}</p>}
        </div>

        {step === STEPS.EXPIRED && (
          <Card data-testid="return-expired">
            <CardContent className="py-8 text-center space-y-2">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
              <p className="text-slate-700">{errMsg || 'This link is no longer valid.'}</p>
              <p className="text-sm text-slate-500">Please contact your supervisor for a new link.</p>
            </CardContent>
          </Card>
        )}

        {step === STEPS.EMAIL && (
          <Card data-testid="return-step-email">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Verify it's you</CardTitle>
              <CardDescription>Enter the email on file to receive a one-time passcode.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={requestOtp} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="return-email">Email</Label>
                  <Input id="return-email" type="email" autoFocus value={email}
                         onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                         data-testid="return-email-input" />
                </div>
                {errMsg && <p className="text-sm text-red-600" data-testid="return-email-error">{errMsg}</p>}
                <Button type="submit" disabled={busy} className="w-full" data-testid="return-request-otp">
                  {busy ? 'Sending…' : 'Send passcode'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === STEPS.OTP && (
          <Card data-testid="return-step-otp">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Enter passcode</CardTitle>
              <CardDescription>We emailed a 6-digit code to {email}.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={verifyOtp} className="space-y-3">
                <Input inputMode="numeric" pattern="[0-9]*" autoFocus maxLength={6} value={otp}
                       onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                       placeholder="123456" className="text-center text-2xl tracking-[0.4em] font-mono"
                       data-testid="return-otp-input" />
                {errMsg && <p className="text-sm text-red-600" data-testid="return-otp-error">{errMsg}</p>}
                <Button type="submit" disabled={busy} className="w-full" data-testid="return-verify-otp">
                  {busy ? 'Verifying…' : 'Verify'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === STEPS.ITEMS && data && (
          <ReturnUpload token={token} scopedToken={scopedToken} data={data} />
        )}
      </div>
    </div>
  );
}

function ReturnUpload({ token, scopedToken, data }) {
  const [items, setItems] = useState(data.items || []);
  const [forms, setForms] = useState(() => {
    const m = {};
    (data.items || []).forEach((it) => {
      const e = it.entered || {};
      m[it.id || it.item_key] = {
        license_type: e.license_type || (it.allowed_types && it.allowed_types.length === 1 ? it.allowed_types[0] : '') || '',
        state: e.state || '', license_number: '',
        expiration_date: e.expiration_date || '',
        qualification_date: e.qualification_date || '',
        range_or_instructor: e.range_or_instructor || '',
        result: e.result || '', score: e.score || '',
        label: 'front',
      };
    });
    return m;
  });
  const [busyKey, setBusyKey] = useState('');

  const setField = (key, field, value) =>
    setForms((f) => ({ ...f, [key]: { ...f[key], [field]: value } }));
  const markReceived = (key) =>
    setItems((prev) => prev.map((i) => (i.id || i.item_key) === key
      ? { ...i, status: 'received', returned_to_employee: false, reject_reason: null } : i));

  const licenseValid = (key) => {
    const f = forms[key] || {};
    return !!(f.license_type && (f.state || '').trim().length === 2 &&
      (f.license_number || '').trim() && f.expiration_date);
  };
  const qualValid = (key) => {
    const f = forms[key] || {};
    return !!(f.qualification_date && f.qualification_date <= todayISO() &&
      (f.range_or_instructor || '').trim() && (f.result === 'Pass' || f.result === 'Fail') &&
      (!(f.score || '').trim() || (f.score || '').length <= 20));
  };
  const appendEntered = (form, key, kind) => {
    const f = forms[key] || {};
    if (kind === 'license') {
      form.append('license_type', f.license_type || '');
      form.append('state', f.state || '');
      form.append('license_number', f.license_number || '');
      form.append('expiration_date', f.expiration_date || '');
    } else if (kind === 'firearms') {
      form.append('qualification_date', f.qualification_date || '');
      form.append('range_or_instructor', f.range_or_instructor || '');
      form.append('result', f.result || '');
      form.append('score', f.score || '');
    }
  };
  const upload = async (item, file) => {
    const rk = item.id || item.item_key;
    setBusyKey(rk);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('label', (forms[rk] || {}).label || 'front');
      appendEntered(form, rk, item.kind);
      await publicCredentialReturnAPI.uploadCredential(token, scopedToken, item.item_key, form);
      markReceived(rk);
      toast.success(`${item.item_label} uploaded — sent back for review`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      return false;
    } finally { setBusyKey(''); }
  };
  const enterNoCard = async (item) => {
    const rk = item.id || item.item_key;
    if (!qualValid(rk)) return;
    setBusyKey(rk);
    try {
      const f = forms[rk];
      await publicCredentialReturnAPI.enterQualification(token, scopedToken, item.item_key, {
        qualification_date: f.qualification_date, range_or_instructor: f.range_or_instructor,
        result: f.result, score: f.score, state: f.state || '',
      });
      markReceived(rk);
      toast.success('Qualification recorded — sent back for review');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your qualification');
    } finally { setBusyKey(''); }
  };
  const badge = (s) => s === 'received' || s === 'verified' ? 'text-emerald-700' : s === 'rejected' ? 'text-red-700' : 'text-slate-500';

  return (
    <Card data-testid="return-items">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><UploadCloud className="w-4 h-4" /> Documents to replace</CardTitle>
        <CardDescription>
          {data.deadline ? `Please upload by ${formatDate(data.deadline)}.` : 'Please upload a corrected version of each item below.'}
          {!data.available && ' Uploads are temporarily unavailable — contact your supervisor.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((item) => {
          const rk = item.id || item.item_key;
          const f = forms[rk] || {};
          const needsReplace = item.status === 'rejected';
          return (
            <div key={rk}
                 className={`border rounded-md p-3 space-y-2 ${needsReplace ? 'border-red-200 bg-red-50/40' : 'border-slate-200'}`}
                 data-testid={`return-cred-${rk}`}>
              <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium text-slate-800">{item.item_label}</div>
                <span className="text-xs rounded-full bg-indigo-100 text-indigo-800 px-2 py-0.5" data-testid={`return-cred-badge-${rk}`}>
                  {item.credential_type || item.item_label}{item.state ? ` · ${item.state}` : ''}
                </span>
              </div>
                <div className={`text-xs ${badge(item.status)}`} data-testid={`return-cred-status-${rk}`}>
                  {item.status === 'received' ? 'Received — thank you'
                    : item.status === 'verified' ? 'Verified'
                    : item.status === 'rejected' ? 'Please replace' : 'Not yet provided'}
                </div>
              </div>
              {needsReplace && item.reject_reason && (
                <div className="text-xs text-red-700 bg-red-100 rounded px-2 py-1" data-testid={`return-cred-reason-${rk}`}>
                  Reason: {item.reject_reason}
                </div>
              )}
              {item.kind === 'firearms' && item.texas_note && (
                <p className={`text-xs rounded px-2 py-1 ${item.texas_prominent ? 'bg-amber-100 text-amber-900 font-medium border border-amber-300' : 'text-slate-600 bg-slate-50'}`}
                   data-testid={`return-cred-texas-${rk}`}>{item.texas_note}</p>
              )}

              {data.available && item.kind === 'license' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {item.allowed_types && item.allowed_types.length > 1 ? (
                      <select className="h-9 rounded border border-slate-300 px-2 text-sm bg-white text-slate-900"
                              value={f.license_type} onChange={(e) => setField(rk, 'license_type', e.target.value)}
                              data-testid={`return-cred-type-${rk}`}>
                        <option value="">Select type</option>
                        {item.allowed_types.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    ) : (
                      <Input value={f.license_type} placeholder="Type"
                             onChange={(e) => setField(rk, 'license_type', e.target.value)}
                             data-testid={`return-cred-type-${rk}`} />
                    )}
                    <Input value={f.state} maxLength={2} placeholder="State (e.g. TX)"
                           onChange={(e) => setField(rk, 'state', e.target.value.toUpperCase())}
                           data-testid={`return-cred-state-${rk}`} />
                    <Input value={f.license_number} placeholder="License number"
                           onChange={(e) => setField(rk, 'license_number', e.target.value)}
                           data-testid={`return-cred-number-${rk}`} />
                  </div>
                  <LabeledDate id={`return-cred-exp-input-${rk}`} label={`${item.item_label} expiration date`}
                               value={f.expiration_date}
                               onChange={(e) => setField(rk, 'expiration_date', e.target.value)}
                               testid={`return-cred-exp-${rk}`} />
                </div>
              )}

              {data.available && item.kind === 'firearms' && (
                <div className="space-y-2">
                  <LabeledDate id={`return-cred-qdate-input-${rk}`} label={`${item.item_label} qualification date`}
                               value={f.qualification_date} max={todayISO()}
                               onChange={(e) => setField(rk, 'qualification_date', e.target.value)}
                               testid={`return-cred-qdate-${rk}`} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={f.range_or_instructor} placeholder="Range or instructor"
                           onChange={(e) => setField(rk, 'range_or_instructor', e.target.value)}
                           data-testid={`return-cred-range-${rk}`} />
                    <select className="h-9 rounded border border-slate-300 px-2 text-sm bg-white text-slate-900"
                            value={f.result} onChange={(e) => setField(rk, 'result', e.target.value)}
                            data-testid={`return-cred-result-${rk}`}>
                      <option value="">Pass or Fail</option>
                      <option value="Pass">Pass</option>
                      <option value="Fail">Fail</option>
                    </select>
                    <Input value={f.score} maxLength={20} placeholder="Score (optional)"
                           onChange={(e) => setField(rk, 'score', e.target.value)}
                           data-testid={`return-cred-score-${rk}`} />
                  </div>
                </div>
              )}

              {data.available && (
                <div className="space-y-2">
                  <StagedUpload testid={`return-credup-${rk}`} accept=".jpg,.jpeg,.png,.heic,.pdf"
                                label={f.label} onLabelChange={(v) => setField(rk, 'label', v)}
                                detailsValid={!((item.kind === 'license' && !licenseValid(rk)) || (item.kind === 'firearms' && !qualValid(rk)))}
                                busy={busyKey === rk} onUpload={(file) => upload(item, file)}
                                chooseLabel={item.kind === 'firearms' ? 'Choose card photo' : 'Choose file'}
                                extra={item.kind === 'firearms' ? (
                                  <Button type="button" size="sm" variant="ghost"
                                          disabled={busyKey === rk || !qualValid(rk)}
                                          onClick={() => enterNoCard(item)}
                                          data-testid={`return-cred-nocard-${rk}`}>
                                    Enter without card
                                  </Button>
                                ) : null} />
                  {(item.status === 'received' || item.status === 'verified') && (
                    <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Sent for review</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
